import { SubmitHandler } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faChartMixed } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createReport } from 'api/reports';
import { useFetchQueryParams } from 'assets/js/pages/reports/useFetchQueryParams';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface SaveAsNewReportDialogProps extends Dialog.DialogProps {}

interface SaveAsReportFormValues {
    name: string;
    description: string;
}

const SaveAsNewReportDialog = (props: SaveAsNewReportDialogProps) => {
    const { children, ...rest } = props;
    const queryClient = useQueryClient();
    const { data: report } = useCurrentReport();
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const { error, success } = useToast();
    const params = useFetchQueryParams();

    const methods = Form.useGivebutterForm<SaveAsReportFormValues>({
        defaultValues: {
            name: report.name ?? '',
        },
    });
    const { control, path, handleSubmit, resetFormButton } = methods;

    const { isPending, mutate: submit } = useMutation({
        mutationFn: (values: SaveAsReportFormValues) =>
            createReport(accountId, {
                name: values.name,
                description: values.description,
                template_id: report.id,
                resource: report.resource,
                query: {
                    columns: params?.columns,
                    display_settings: params?.display_settings,
                    filters: params?.filters,
                    graph_settings: params?.graph_settings,
                    date_range: params?.date_range,
                },
            }),
        onSuccess: report => {
            history.push(`/accounts/${accountId}/reports/${report.id}`);
            success('Successfully created report!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.reports(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Unable to create report');
            resetFormButton();
        },
    });

    return (
        <Dialog.Root {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Form.Provider {...methods}>
                        <form
                            className={'contents'}
                            onSubmit={handleSubmit(submit as SubmitHandler<SaveAsReportFormValues>)}
                        >
                            <Dialog.Close />
                            <div className={'flex flex-col gap-6'}>
                                <Avatar.Root>
                                    <Avatar.Fallback>
                                        <FontAwesomeIcon icon={faChartMixed} />
                                    </Avatar.Fallback>
                                </Avatar.Root>
                                <div className={'flex flex-col gap-2'}>
                                    <p className={'text-xl font-medium'}>Save report as</p>
                                    <p className={'text-sm text-gray-600'}>
                                        Saving as new will apply any additional filters or unsaved
                                        changes to your new report, allowing you to create a new
                                        version without modifying the original.
                                    </p>
                                </div>
                                <Form.Container>
                                    <Form.Label name={path('name')}>Report name</Form.Label>
                                    <Form.TextInputRoot
                                        control={control}
                                        name={'name'}
                                        rules={{ required: 'Please enter a name for the report' }}
                                    >
                                        <Form.TextInputForm />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('name')} />
                                </Form.Container>
                                <Form.Container>
                                    <Form.Label name={path('description')}>Description</Form.Label>
                                    <Form.Textarea
                                        rows={6}
                                        control={control}
                                        name={'description'}
                                    />
                                    <Form.Error name={path('description')} />
                                </Form.Container>
                                <div
                                    className={
                                        'mt-4 flex w-full flex-wrap-reverse items-center gap-3'
                                    }
                                >
                                    <div className={'flex-1'}>
                                        <Dialog.Cancel asChild={true}>
                                            <Button
                                                className={'w-full'}
                                                variant={'outline'}
                                                color={'neutral'}
                                            >
                                                Cancel
                                            </Button>
                                        </Dialog.Cancel>
                                    </div>
                                    <div className={'flex-1'}>
                                        <Form.SubmitButton
                                            className={'w-full'}
                                            isSubmitting={isPending}
                                        >
                                            Create
                                        </Form.SubmitButton>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const SaveAsNewReportDialogTrigger = Dialog.Trigger;

const Root = SaveAsNewReportDialog;
const Trigger = SaveAsNewReportDialogTrigger;

export {
    SaveAsNewReportDialog,
    SaveAsNewReportDialogTrigger,
    //
    Root,
    Trigger,
};
